<template>
  <b-container fluid class="report-target-page py-3">
    <div class="iq-card">
      <div class="iq-card-header d-flex align-items-center justify-content-between">
        <div class="iq-header-title py-2">
          <h4 class="card-title mb-0">Daily Report</h4>
          <small class="text-muted">Recipients and asset report schedules</small>
        </div>
        <div class="header-actions">
          <b-button variant="outline-secondary" size="sm" :to="{ name: 'MasterConfiguration' }">
            Back
          </b-button>
          <b-button variant="primary" size="sm" :disabled="isLoading" @click="loadData">
            Refresh
          </b-button>
        </div>
      </div>

      <div class="iq-card-body">
        <b-alert v-if="message.text" show :variant="message.variant" class="mb-3">
          {{ message.text }}
        </b-alert>

        <b-tabs v-model="activeTab" content-class="pt-3">
          <b-tab title="Recipients">
            <section class="panel">
              <div class="table-toolbar">
                <div>
                  <h5 class="panel-title mb-1">Assets</h5>
                  <small class="text-muted">{{ targetPagination.totalData || targets.length }} recipients</small>
                </div>
                <div class="table-actions">
                  <div class="pagination-control">
                    <b-form-select
                      v-model="targetPagination.itemPerPage"
                      :options="pageSizeOptions"
                      size="sm"
                      :disabled="isLoading"
                      @change="onPageSizeChanged('target')"
                    />
                  </div>
                  <b-button variant="success" size="sm" :disabled="isLoading" @click="openTargetModal()">
                    Add Recipient
                  </b-button>
                </div>
              </div>

              <div class="filter-panel">
                <b-row>
                  <b-col md="6" xl="3" class="mb-2">
                    <b-form-group label="Company" label-for="recipient-company" class="mb-0">
                      <b-form-select
                        id="recipient-company"
                        :value="activeCompanyId"
                        :options="companyOptions"
                        :disabled="isLoading"
                        @change="onCompanyChanged"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col md="6" xl="3" class="mb-2">
                    <b-form-group label="Asset" class="mb-0">
                      <searchable-select
                        v-model="targetFilters.asset"
                        :fetcher="searchAssets"
                        placeholder="All assets"
                        :disabled="isLoading"
                        @input="onAssetFilterChanged('target', $event)"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
                <div class="filter-actions">
                  <b-button variant="outline-secondary" size="sm" :disabled="isLoading" @click="resetFilters('target')">
                    Reset
                  </b-button>
                </div>
              </div>

              <b-table
                responsive
                hover
                bordered
                small
                :busy="isLoading"
                :items="groupedTargets"
                :fields="targetFields"
                tbody-tr-class="asset-row"
                show-empty
                empty-text="No report recipient data."
                @row-clicked="toggleTargetGroup"
              >
                <template #cell(asset)="row">
                  <div class="asset-cell">
                    <div class="cell-title asset-title">
                      <i :class="row.detailsShowing ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'"></i>
                      <span>{{ getTargetAssetLabel(row.item) }}</span>
                    </div>
                    <b-button
                      variant="outline-primary"
                      size="sm"
                      :disabled="isLoading || !row.item.items.length"
                      @click.stop="openDuplicateTargetModal(row.item)"
                    >
                      Duplicate
                    </b-button>
                  </div>
                </template>
                <template #cell(count)="row">
                  <b-badge variant="info">{{ row.item.items.length }}</b-badge>
                </template>
                <template #cell(summary)="row">
                  <span class="text-muted">{{ getRecipientSummary(row.item.items) }}</span>
                </template>
                <template #row-details="row">
                  <div class="asset-rule-list">
                    <div class="detail-toolbar">
                      <div class="detail-selection">
                        <b-form-checkbox
                          :checked="isAllGroupSelected('target', row.item)"
                          :indeterminate="isSomeGroupSelected('target', row.item)"
                          :disabled="isLoading || !row.item.items.length"
                          @change="toggleGroupSelection('target', row.item, $event)"
                        >
                          <small class="text-muted">
                            {{ getSelectedGroupCount('target', row.item) }} selected / {{ row.item.items.length }} recipients
                          </small>
                        </b-form-checkbox>
                      </div>
                      <div class="row-actions">
                        <b-button
                          variant="outline-danger"
                          size="sm"
                          :disabled="isLoading || !getSelectedGroupCount('target', row.item)"
                          @click.stop="confirmBulkDelete('target', row.item)"
                        >
                          Delete Selected
                        </b-button>
                      </div>
                    </div>
                    <div
                      v-for="item in row.item.items"
                      :key="item.rptrId"
                      class="rule-item"
                    >
                      <div class="rule-main">
                        <div>
                          <b-form-checkbox
                            :checked="isSelected('target', item.rptrId)"
                            :disabled="isLoading"
                            @change="toggleItemSelection('target', item.rptrId, $event)"
                          />
                        </div>
                        <div class="recipient-inline">
                          <span class="cell-title">{{ item.rptrName || '-' }}</span>
                          <small class="text-muted">{{ item.rptrEmail || '-' }}</small>
                        </div>
                      </div>
                      <div class="row-actions">
                        <b-button variant="outline-primary" size="sm" @click.stop="openTargetModal(item)">Edit</b-button>
                        <b-button variant="outline-danger" size="sm" @click.stop="confirmDelete('target', item)">Delete</b-button>
                      </div>
                    </div>
                  </div>
                </template>
              </b-table>

              <div class="d-flex justify-content-end mt-3">
                <b-pagination
                  v-model="targetPagination.page"
                  :total-rows="targetPagination.totalData"
                  :per-page="targetPagination.itemPerPage"
                  size="sm"
                  :disabled="isLoading"
                  @change="onPageChanged('target', $event)"
                />
              </div>
            </section>
          </b-tab>

          <b-tab title="Asset Schedules">
            <section class="panel">
              <div class="table-toolbar">
                <div>
                  <h5 class="panel-title mb-1">Assets</h5>
                  <small class="text-muted">{{ assetPagination.totalData || targetAssets.length }} schedules</small>
                </div>
                <div class="table-actions">
                  <div class="pagination-control">
                    <b-form-select
                      v-model="assetPagination.itemPerPage"
                      :options="pageSizeOptions"
                      size="sm"
                      :disabled="isLoading"
                      @change="onPageSizeChanged('asset')"
                    />
                  </div>
                  <b-button variant="success" size="sm" :disabled="isLoading" @click="openAssetModal()">
                    Add Schedule
                  </b-button>
                </div>
              </div>

              <div class="filter-panel">
                <b-row>
                  <b-col md="6" xl="3" class="mb-2">
                    <b-form-group label="Company" label-for="schedule-company" class="mb-0">
                      <b-form-select
                        id="schedule-company"
                        :value="activeCompanyId"
                        :options="companyOptions"
                        :disabled="isLoading"
                        @change="onCompanyChanged"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col md="6" xl="3" class="mb-2">
                    <b-form-group label="Asset" class="mb-0">
                      <searchable-select
                        v-model="assetFilters.asset"
                        :fetcher="searchAssets"
                        placeholder="All assets"
                        :disabled="isLoading"
                        @input="onAssetFilterChanged('asset', $event)"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
                <div class="filter-actions">
                  <b-button variant="outline-secondary" size="sm" :disabled="isLoading" @click="resetFilters('asset')">
                    Reset
                  </b-button>
                </div>
              </div>

              <b-table
                responsive
                hover
                bordered
                small
                :busy="isLoading"
                :items="groupedTargetAssets"
                :fields="assetFields"
                tbody-tr-class="asset-row"
                show-empty
                empty-text="No report target asset data."
                @row-clicked="toggleAssetGroup"
              >
                <template #cell(asset)="row">
                  <div class="asset-cell">
                    <div class="cell-title asset-title">
                      <i :class="row.detailsShowing ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'"></i>
                      <span>{{ getScheduleAssetLabel(row.item) }}</span>
                    </div>
                    <b-button
                      variant="outline-primary"
                      size="sm"
                      :disabled="isLoading || !row.item.items.length"
                      @click.stop="openDuplicateAssetScheduleModal(row.item)"
                    >
                      Duplicate
                    </b-button>
                  </div>
                </template>
                <template #cell(count)="row">
                  <b-badge variant="info">{{ row.item.items.length }}</b-badge>
                </template>
                <template #cell(schedule)="row">
                  <span class="text-muted">{{ getScheduleSummary(row.item.items) }}</span>
                </template>
                <template #row-details="row">
                  <div class="asset-rule-list">
                    <div class="detail-toolbar">
                      <div class="detail-selection">
                        <b-form-checkbox
                          :checked="isAllGroupSelected('asset', row.item)"
                          :indeterminate="isSomeGroupSelected('asset', row.item)"
                          :disabled="isLoading || !row.item.items.length"
                          @change="toggleGroupSelection('asset', row.item, $event)"
                        >
                          <small class="text-muted">
                            {{ getSelectedGroupCount('asset', row.item) }} selected / {{ row.item.items.length }} schedules
                          </small>
                        </b-form-checkbox>
                      </div>
                      <div class="row-actions">
                        <b-button
                          variant="outline-danger"
                          size="sm"
                          :disabled="isLoading || !getSelectedGroupCount('asset', row.item)"
                          @click.stop="confirmBulkDelete('asset', row.item)"
                        >
                          Delete Selected
                        </b-button>
                      </div>
                    </div>
                    <div
                      v-for="item in row.item.items"
                      :key="item.mrtaId"
                      class="rule-item"
                    >
                      <div class="rule-main">
                        <div>
                          <b-form-checkbox
                            :checked="isSelected('asset', item.mrtaId)"
                            :disabled="isLoading"
                            @change="toggleItemSelection('asset', item.mrtaId, $event)"
                          />
                        </div>
                        <div>
                          <b-badge :variant="item.mrtaStatusGenerateManual ? 'success' : 'secondary'">
                            {{ item.mrtaStatusGenerateManual ? 'Enabled' : 'Disabled' }}
                          </b-badge>
                        </div>
                        <div>
                          <span class="cell-title">{{ getFrequencyLabel(item.mrtaFrequencyType) }}</span>
                          <small class="text-muted d-block">{{ item.mrtaTime }} | {{ item.mrtaTimeFrom }} - {{ item.mrtaTimeTo }}</small>
                        </div>
                        <div>
                          <small class="text-muted">UTC+{{ item.mrtaTimezone || 7 }} | Min: {{ item.mrtaDataThresholdMin }}%</small>
                        </div>
                      </div>
                      <div class="row-actions">
                        <b-button variant="outline-primary" size="sm" @click.stop="openAssetModal(item)">Edit</b-button>
                        <b-button variant="outline-danger" size="sm" @click.stop="confirmDelete('asset', item)">Delete</b-button>
                      </div>
                    </div>
                  </div>
                </template>
              </b-table>

              <div class="d-flex justify-content-end mt-3">
                <b-pagination
                  v-model="assetPagination.page"
                  :total-rows="assetPagination.totalData"
                  :per-page="assetPagination.itemPerPage"
                  size="sm"
                  :disabled="isLoading"
                  @change="onPageChanged('asset', $event)"
                />
              </div>
            </section>
          </b-tab>

          <b-tab title="Delivery Logs">
            <section class="panel">
              <div class="table-toolbar">
                <div>
                  <h5 class="panel-title mb-1">Report Delivery History</h5>
                  <small class="text-muted">Read-only worker results</small>
                </div>
              </div>

              <div class="filter-panel">
                <b-row>
                  <b-col md="6" xl="3" class="mb-2">
                    <b-form-group label="Company" label-for="log-company" class="mb-0">
                      <b-form-select
                        id="log-company"
                        :value="activeCompanyId"
                        :options="companyOptions"
                        :disabled="isLoading"
                        @change="onCompanyChanged"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col md="6" xl="3" class="mb-2">
                    <b-form-group label="Asset" class="mb-0">
                      <searchable-select
                        v-model="logFilters.asset"
                        :fetcher="searchAssets"
                        placeholder="Select asset"
                        :disabled="isLoading"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col md="12" xl="3" class="mb-2">
                    <b-form-group label="Select Range" class="mb-0">
                      <date-time-picker v-model="logFilters.dateRange" is-range :masks="dateMasks" :disabled="isLoading" />
                    </b-form-group>
                  </b-col>
                  <b-col md="6" xl="3" class="mb-2">
                    <b-form-group label="Status" label-for="log-status" class="mb-0">
                      <b-form-select id="log-status" v-model="logFilters.status" :options="logStatusOptions" :disabled="isLoading" />
                    </b-form-group>
                  </b-col>
                </b-row>
              </div>

              <b-table
                responsive
                hover
                bordered
                small
                :busy="isLoading"
                :items="filteredDeliveryLogs"
                :fields="logFields"
                show-empty
                :empty-text="deliveryLogEmptyText"
              >
                <template #cell(frequency)="row">
                  {{ getFrequencyLabel(row.item.mrtlFrequencyType) }}
                </template>
                <template #cell(period)="row">
                  {{ formatLogPeriod(row.item.mrtlPeriodStart) }} - {{ formatLogPeriod(row.item.mrtlPeriodEnd) }}
                </template>
                <template #cell(generatedAt)="row">
                  {{ formatGeneratedAt(row.item.mrtlGeneratedAt) }}
                </template>
                <template #cell(status)="row">
                  <b-badge :variant="getLogStatusVariant(row.item.mrtlStatus)">
                    {{ row.item.mrtlStatus || '-' }}
                  </b-badge>
                </template>
                <template #cell(message)="row">
                  {{ row.item.mrtlMessage || '-' }}
                </template>
              </b-table>
            </section>
          </b-tab>
        </b-tabs>
      </div>
    </div>

    <b-modal
      v-model="targetModal.visible"
      :title="targetModal.id ? 'Edit recipient' : 'Add recipient'"
      hide-footer
      lazy
    >
      <b-form @submit.prevent="saveTarget">
        <b-form-group label="Asset">
          <searchable-select
            v-model="targetForm.asset"
            :fetcher="searchAssets"
            placeholder="Select asset"
            :disabled="isLoading"
          />
        </b-form-group>
        <b-form-group label="Name" label-for="rptr-name">
          <b-form-input id="rptr-name" v-model.trim="targetForm.rptrName" maxlength="120" required />
        </b-form-group>
        <b-form-group label="Email" label-for="rptr-email">
          <b-form-input id="rptr-email" v-model.trim="targetForm.rptrEmail" type="email" maxlength="120" required />
        </b-form-group>
        <div class="modal-actions">
          <b-button variant="outline-secondary" :disabled="isLoading" @click="targetModal.visible = false">
            Cancel
          </b-button>
          <b-button type="submit" variant="success" :disabled="isLoading || !canSaveTarget">Save</b-button>
        </div>
      </b-form>
    </b-modal>

    <b-modal
      v-model="assetModal.visible"
      :title="assetModal.id ? 'Edit asset schedule' : 'Add asset schedule'"
      hide-footer
      lazy
      size="lg"
    >
      <b-form @submit.prevent="saveAsset">
        <b-row>
          <b-col md="6">
            <b-form-group label="Asset">
              <searchable-select
                v-model="assetForm.asset"
                :fetcher="searchAssets"
                placeholder="Select asset"
                :disabled="isLoading"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Generate Manual" label-for="mrta-manual">
              <b-form-select id="mrta-manual" v-model="assetForm.mrtaStatusGenerateManual" :options="binaryOptions" />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Frequency Type" label-for="mrta-frequency">
              <b-form-select id="mrta-frequency" v-model="assetForm.mrtaFrequencyType" :options="frequencyOptions" />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Timezone" label-for="mrta-timezone">
              <b-form-select id="mrta-timezone" v-model="assetForm.mrtaTimezone" :options="timezoneOptions" />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Chart Aggregation" label-for="mrta-aggregated-unit">
              <b-form-select id="mrta-aggregated-unit" v-model="assetForm.mrtaAggregatedUnit" :options="aggregatedUnitOptions" />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Time" label-for="mrta-time">
              <b-form-input id="mrta-time" v-model="assetForm.mrtaTime" type="time" step="1" required />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Time From" label-for="mrta-time-from">
              <b-form-input id="mrta-time-from" v-model="assetForm.mrtaTimeFrom" type="time" step="1" required />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Time To" label-for="mrta-time-to">
              <b-form-input id="mrta-time-to" v-model="assetForm.mrtaTimeTo" type="time" step="1" required />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Day Of Week" label-for="mrta-day-week">
              <NumberInput id="mrta-day-week" v-model="assetForm.mrtaDayOfWeek" class="form-control" :decimals="0" :min="0" :max="6" required />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Date Of Month" label-for="mrta-date-month">
              <NumberInput id="mrta-date-month" v-model="assetForm.mrtaDateOfMonth" class="form-control" :decimals="0" :min="-31" :max="31" />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Data Threshold Min" label-for="mrta-threshold">
              <NumberInput id="mrta-threshold" v-model="assetForm.mrtaDataThresholdMin" class="form-control" :decimals="0" :min="0" :max="100" />
            </b-form-group>
          </b-col>
        </b-row>
        <div class="modal-actions">
          <b-button variant="outline-secondary" :disabled="isLoading" @click="assetModal.visible = false">
            Cancel
          </b-button>
          <b-button type="submit" variant="success" :disabled="isLoading || !canSaveAsset">Save</b-button>
        </div>
      </b-form>
    </b-modal>

    <b-modal
      v-model="deleteModal.visible"
      title="Delete data"
      ok-title="Delete"
      ok-variant="danger"
      :ok-disabled="isLoading"
      :cancel-disabled="isLoading"
      @ok.prevent="deleteSelected"
    >
      {{ deleteModal.message || `Delete selected ${deleteModal.type} data?` }}
    </b-modal>

    <b-modal
      v-model="duplicateModal.visible"
      title="Duplicate data"
      ok-title="Duplicate"
      ok-variant="primary"
      :ok-disabled="isLoading || !canDuplicate"
      :cancel-disabled="isLoading"
      @ok.prevent="duplicateSelected"
    >
      <div class="mb-3">
        <div class="cell-title">{{ duplicateModal.sourceLabel || '-' }}</div>
        <small class="text-muted">Select target assets for duplication.</small>
      </div>

      <b-form-group label="Target Assets" class="mb-0">
        <searchable-select
          v-model="duplicateModal.targetAssets"
          :fetcher="searchDuplicateTargetAssets"
          placeholder="Select target assets"
          :disabled="isLoading"
          multiple
        />
      </b-form-group>
    </b-modal>
  </b-container>
</template>

<script>
import AssetSvc from '@/services/AssetSvc';
import ReportAssetLogSvc from '@/services/ReportAssetLogSvc';
import ReportTargetSvc from '@/services/ReportTargetSvc';
import ReportTargetAssetSvc from '@/services/ReportTargetAssetSvc';
import SearchableSelect from '@/components/master/SearchableSelect.vue';
import DateTimePicker from '@/components/directives/DateTimePicker.vue';
import NumberInput from '@/components/directives/NumberInput.vue';
import { findCompany } from '@/util/companies';
import { PAGE_SIZE_OPTIONS } from '@/util/master/masterQuery';

const SEARCH_RESULT_LIMIT = 50;
const DUPLICATE_RESULT_LIMIT = 1000;
const ACTIVE_ASSET_LIMIT = 1000;

function createPagination() {
  return { page: 1, itemPerPage: 50, totalData: 0 };
}

function createLogDateRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 7);
  start.setHours(0, 0, 0, 0);
  return { start, end };
}

export default {
  name: 'DailyReport',
  components: {
    NumberInput,
    SearchableSelect,
    DateTimePicker,
  },
  data() {
    return {
      activeTab: Number(sessionStorage.getItem('daily-report-active-tab') || 0),
      targetFilters: { asset: null },
      assetFilters: { asset: null },
      activeAssetIds: [],
      targets: [],
      targetAssets: [],
      deliveryLogs: [],
      logsReqId: 0,
      logFilters: {
        asset: null,
        dateRange: createLogDateRange(),
        status: '',
      },
      dateMasks: 'DD-MM-YYYY HH:mm',
      targetPagination: createPagination(),
      assetPagination: createPagination(),
      targetGroupOpenMap: {},
      assetGroupOpenMap: {},
      selectedTargetMap: {},
      selectedAssetMap: {},
      isLoading: false,
      message: { text: '', variant: 'success' },
      targetModal: { visible: false, id: null },
      assetModal: { visible: false, id: null },
      deleteModal: {
        visible: false, type: '', id: null, ids: [], message: '',
      },
      duplicateModal: {
        visible: false,
        type: '',
        sourceAsset: null,
        sourceLabel: '',
        targetAssets: [],
      },
      targetForm: { asset: null, rptrName: '', rptrEmail: '' },
      assetForm: this.getDefaultAssetForm(),
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      binaryOptions: [
        { value: 0, text: 'Disabled' },
        { value: 1, text: 'Enabled' },
      ],
      frequencyOptions: [
        { value: 2, text: 'Daily' },
        { value: 3, text: 'Weekly' },
        { value: 4, text: 'Monthly' },
      ],
      timezoneOptions: [
        { value: 7, text: 'WIB (UTC+7)' },
        { value: 8, text: 'WITA (UTC+8)' },
        { value: 9, text: 'WIT (UTC+9)' },
      ],
      aggregatedUnitOptions: [
        { value: '', text: 'Automatic' },
        { value: 'HOUR', text: 'Hour' },
        { value: 'DAY', text: 'Day' },
      ],
      logStatusOptions: [
        { value: '', text: 'All statuses' },
        { value: 'SUCCESS', text: 'Success' },
        { value: 'NO_DATA', text: 'No data' },
        { value: 'ERROR', text: 'Error' },
      ],
      targetFields: [
        { key: 'asset', label: 'Asset' },
        { key: 'count', label: 'Recipients' },
        { key: 'summary', label: 'Summary' },
      ],
      assetFields: [
        { key: 'asset', label: 'Asset' },
        { key: 'count', label: 'Schedules' },
        { key: 'schedule', label: 'Schedule' },
      ],
      logFields: [
        { key: 'frequency', label: 'Frequency' },
        { key: 'period', label: 'Report Period' },
        { key: 'generatedAt', label: 'Generated At (UTC)' },
        { key: 'status', label: 'Status' },
        { key: 'message', label: 'Message' },
      ],
    };
  },
  computed: {
    canSaveTarget() {
      return Boolean(this.targetForm.asset && this.targetForm.rptrName && this.targetForm.rptrEmail);
    },
    canSaveAsset() {
      const form = this.assetForm;
      return Boolean(
        form.asset
        && [2, 3, 4].includes(Number(form.mrtaFrequencyType))
        && [7, 8, 9].includes(Number(form.mrtaTimezone))
        && form.mrtaTime
        && form.mrtaTimeFrom
        && form.mrtaTimeTo
        && Number(form.mrtaDayOfWeek) >= 0
        && Number(form.mrtaDayOfWeek) <= 6,
      );
    },
    canDuplicate() {
      return Boolean(this.duplicateModal.sourceAsset && this.duplicateModal.targetAssets.length);
    },
    canLoadLogs() {
      return Boolean(
        this.logFilters.asset
        && this.logFilters.dateRange
        && this.logFilters.dateRange.start
        && this.logFilters.dateRange.end
        && this.logFilters.dateRange.start <= this.logFilters.dateRange.end,
      );
    },
    filteredDeliveryLogs() {
      return this.logFilters.status
        ? this.deliveryLogs.filter((item) => item.mrtlStatus === this.logFilters.status)
        : this.deliveryLogs;
    },
    deliveryLogEmptyText() {
      return this.logFilters.asset
        ? 'No delivery logs found for the selected filters.'
        : 'Select an asset to load delivery logs.';
    },
    activeCompanyId() {
      const company = this.$store.state.auth.activeCompany;
      return company ? company.compId : null;
    },
    companyOptions() {
      return Object.values(this.$store.state.auth.availableCompany || {}).map((company) => ({
        value: company.compId,
        text: company.compName,
      }));
    },
    groupedTargets() {
      const groups = {};
      this.targets.forEach((item) => {
        const key = item.rptrMassId || 'unknown';
        if (!groups[key]) {
          groups[key] = {
            key,
            massId: item.rptrMassId,
            assetName: this.getTargetAssetName(item),
            items: [],
          };
        }
        groups[key].items.push(item);
      });
      return Object.values(groups).map((group) => ({
        ...group,
        _showDetails: Boolean(this.targetGroupOpenMap[group.key]),
      }));
    },
    groupedTargetAssets() {
      const groups = {};
      this.targetAssets.forEach((item) => {
        const key = item.mrtaMassId || 'unknown';
        if (!groups[key]) {
          groups[key] = {
            key,
            massId: item.mrtaMassId,
            assetName: this.getScheduleAssetName(item),
            items: [],
          };
        }
        groups[key].items.push(item);
      });
      return Object.values(groups).map((group) => ({
        ...group,
        _showDetails: Boolean(this.assetGroupOpenMap[group.key]),
      }));
    },
  },
  watch: {
    'logFilters.asset': 'fetchDeliveryLogs',
    'logFilters.dateRange': {
      deep: true,
      handler: 'fetchDeliveryLogs',
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    buildParams(pagination, finds) {
      const params = { page: pagination.page, max: pagination.itemPerPage };
      Object.keys(finds).forEach((key) => {
        const value = finds[key];
        if (value !== null && value !== undefined && value !== '') {
          params[`finds[${key}]`] = value;
        }
      });
      return params;
    },
    buildSearchParams(field, query) {
      const params = { max: SEARCH_RESULT_LIMIT };
      if (query) {
        params[`finds[${field}]`] = query;
      }
      return params;
    },
    clearMessage() {
      this.message = { text: '', variant: 'success' };
    },
    confirmDelete(type, item) {
      const idMap = { target: 'rptrId', asset: 'mrtaId' };
      this.deleteModal = {
        visible: true,
        type,
        id: item[idMap[type]],
        ids: [],
        message: `Delete selected ${type} data?`,
      };
    },
    confirmBulkDelete(type, group) {
      const ids = this.getGroupItemIds(type, group).filter((id) => this.isSelected(type, id));
      if (!ids.length) return;
      this.deleteModal = {
        visible: true,
        type,
        id: null,
        ids,
        message: `Delete ${ids.length} selected ${type === 'target' ? 'recipient' : 'schedule'} data?`,
      };
    },
    deleteSelected() {
      const services = { target: ReportTargetSvc, asset: ReportTargetAssetSvc };
      const kind = this.deleteModal.type;
      return this.runAction(async () => {
        const ids = this.deleteModal.ids && this.deleteModal.ids.length ? this.deleteModal.ids : [this.deleteModal.id];
        await Promise.all(ids.map((id) => services[kind].delete(id)));
        this.clearSelection(kind, ids);
        this.deleteModal.visible = false;
        await this.fetchFor(kind);
      }, 'Data deleted.');
    },
    extractError(error) {
      const response = error && error.response;
      if (response && response.data && response.data.status && response.data.status.message) {
        return response.data.status.message;
      }
      return (error && error.message) || 'Request failed';
    },
    fetchFor(kind) {
      return kind === 'target' ? this.fetchTargets() : this.fetchTargetAssets();
    },
    async fetchTargets() {
      const assetIds = this.getFilterAssetIds('target');
      if (!assetIds.length) {
        this.targets = [];
        this.targetPagination.totalData = 0;
        return;
      }
      const finds = { rptrMassId: assetIds };
      const response = await ReportTargetSvc.get(this.buildParams(this.targetPagination, finds));
      this.targets = this.getRows(response);
      this.targetPagination.totalData = (response.data.pagination || {}).totalData || 0;
    },
    async fetchTargetAssets() {
      const assetIds = this.getFilterAssetIds('asset');
      if (!assetIds.length) {
        this.targetAssets = [];
        this.assetPagination.totalData = 0;
        return;
      }
      const finds = { mrtaMassId: assetIds };
      const response = await ReportTargetAssetSvc.get(this.buildParams(this.assetPagination, finds));
      this.targetAssets = this.getRows(response);
      this.assetPagination.totalData = (response.data.pagination || {}).totalData || 0;
    },
    getDefaultAssetForm() {
      return {
        asset: null,
        mrtaStatusGenerateManual: 0,
        mrtaFrequencyType: 2,
        mrtaTime: '07:00:00',
        mrtaTimezone: 7,
        mrtaTimeFrom: '00:00:00',
        mrtaTimeTo: '23:59:59',
        mrtaDayOfWeek: 0,
        mrtaDateOfMonth: 1,
        mrtaDataThresholdMin: 0,
        mrtaAggregatedUnit: '',
      };
    },
    getFrequencyLabel(value) {
      const option = this.frequencyOptions.find((item) => item.value === Number(value));
      return option ? option.text : value;
    },
    getRows(response) {
      return response && response.data && Array.isArray(response.data.data) ? response.data.data : [];
    },
    getScheduleAssetName(item) {
      return item && item.mrtaMass && item.mrtaMass.massName ? item.mrtaMass.massName : '-';
    },
    getTargetAssetName(item) {
      return item && item.rptrMass && item.rptrMass.massName ? item.rptrMass.massName : '-';
    },
    loadData() {
      return this.runAction(async () => {
        await this.fetchActiveAssets();
        await Promise.all([this.fetchTargets(), this.fetchTargetAssets()]);
      });
    },
    async fetchActiveAssets() {
      const response = await AssetSvc.getAssets({ params: { page: 1, max: ACTIVE_ASSET_LIMIT } });
      this.activeAssetIds = this.getRows(response)
        .map((asset) => asset.massId)
        .filter((id) => id !== null && id !== undefined);
      ['target', 'asset'].forEach((kind) => {
        const filters = kind === 'target' ? this.targetFilters : this.assetFilters;
        if (filters.asset && !this.activeAssetIds.some((id) => String(id) === String(filters.asset.value))) {
          filters.asset = null;
        }
      });
    },
    getFilterAssetIds(kind) {
      const filters = kind === 'target' ? this.targetFilters : this.assetFilters;
      return filters.asset ? [filters.asset.value] : this.activeAssetIds;
    },
    onAssetFilterChanged(kind, asset) {
      const filters = kind === 'target' ? this.targetFilters : this.assetFilters;
      filters.asset = asset;
      this.paginationFor(kind).page = 1;
      return this.refresh(kind);
    },
    async onCompanyChanged(companyId) {
      if (String(companyId) === String(this.activeCompanyId)) return;
      const company = findCompany(this.$store.state.auth.availableCompany, companyId);
      if (!company) return;

      this.isLoading = true;
      this.clearMessage();
      try {
        // ditulis sebelum remount yang membaca tab ini
        sessionStorage.setItem('daily-report-active-tab', this.activeTab);
        await this.$store.dispatch('auth/switchCompany', company);
      } catch (error) {
        this.message = { text: this.extractError(error), variant: 'danger' };
      } finally {
        this.isLoading = false;
      }
    },
    onPageChanged(kind, page) {
      this.paginationFor(kind).page = page;
      return this.refresh(kind);
    },
    onPageSizeChanged(kind) {
      this.paginationFor(kind).page = 1;
      return this.refresh(kind);
    },
    openAssetModal(item) {
      this.assetModal = { visible: true, id: item ? item.mrtaId : null };
      this.assetForm = item
        ? {
          asset: {
            value: item.mrtaMassId,
            text: `${this.getScheduleAssetName(item)} (ID: ${item.mrtaMassId})`,
          },
          mrtaStatusGenerateManual: item.mrtaStatusGenerateManual,
          mrtaFrequencyType: item.mrtaFrequencyType,
          mrtaTime: item.mrtaTime,
          mrtaTimezone: item.mrtaTimezone === null || item.mrtaTimezone === undefined ? 7 : item.mrtaTimezone,
          mrtaTimeFrom: item.mrtaTimeFrom,
          mrtaTimeTo: item.mrtaTimeTo,
          mrtaDayOfWeek: item.mrtaDayOfWeek,
          mrtaDateOfMonth: item.mrtaDateOfMonth,
          mrtaDataThresholdMin: Number(item.mrtaDataThresholdMin || 0),
          mrtaAggregatedUnit: item.mrtaAggregatedUnit || '',
        }
        : this.getDefaultAssetForm();
    },
    openTargetModal(item) {
      this.targetModal = { visible: true, id: item ? item.rptrId : null };
      this.targetForm = item
        ? {
          asset: {
            value: item.rptrMassId,
            text: `${this.getTargetAssetName(item)} (ID: ${item.rptrMassId})`,
          },
          rptrName: item.rptrName,
          rptrEmail: item.rptrEmail,
        }
        : { asset: null, rptrName: '', rptrEmail: '' };
    },
    openDuplicateTargetModal(group) {
      this.duplicateModal = {
        visible: true,
        type: 'target',
        sourceAsset: { value: group.massId, text: this.getTargetAssetLabel(group) },
        sourceLabel: this.getTargetAssetLabel(group),
        targetAssets: [],
      };
    },
    openDuplicateAssetScheduleModal(group) {
      this.duplicateModal = {
        visible: true,
        type: 'asset',
        sourceAsset: { value: group.massId, text: this.getScheduleAssetLabel(group) },
        sourceLabel: this.getScheduleAssetLabel(group),
        targetAssets: [],
      };
    },
    paginationFor(kind) {
      return kind === 'target' ? this.targetPagination : this.assetPagination;
    },
    resetFilters(kind) {
      if (kind === 'target') {
        this.targetFilters = { asset: null };
      } else {
        this.assetFilters = { asset: null };
      }
      this.paginationFor(kind).page = 1;
      return this.refresh(kind);
    },
    refresh(kind) {
      return this.runAction(() => this.fetchFor(kind));
    },
    async runAction(actionFn, successMessage) {
      this.isLoading = true;
      this.clearMessage();
      try {
        await actionFn();
        if (successMessage) {
          this.message = { text: successMessage, variant: 'success' };
        }
      } catch (error) {
        this.message = { text: this.extractError(error), variant: 'danger' };
      } finally {
        this.isLoading = false;
      }
    },
    saveAsset() {
      return this.runAction(async () => {
        const payload = this.buildAssetPayload(this.assetForm, this.assetForm.asset.value);
        if (this.assetModal.id) {
          await ReportTargetAssetSvc.update(this.assetModal.id, payload);
        } else {
          await ReportTargetAssetSvc.create(payload);
        }
        this.assetModal.visible = false;
        await this.fetchTargetAssets();
      }, 'Asset schedule saved.');
    },
    buildAssetPayload(item, massId) {
      return {
        mrtaMassId: massId,
        mrtaStatusGenerateManual: item.mrtaStatusGenerateManual,
        mrtaFrequencyType: item.mrtaFrequencyType,
        mrtaTime: item.mrtaTime,
        mrtaTimezone: item.mrtaTimezone === null || item.mrtaTimezone === undefined ? 7 : item.mrtaTimezone,
        mrtaTimeFrom: item.mrtaTimeFrom,
        mrtaTimeTo: item.mrtaTimeTo,
        mrtaDayOfWeek: item.mrtaDayOfWeek,
        mrtaDateOfMonth: item.mrtaDateOfMonth,
        mrtaDataThresholdMin: item.mrtaDataThresholdMin,
        mrtaAggregatedUnit: item.mrtaAggregatedUnit || '',
      };
    },
    saveTarget() {
      return this.runAction(async () => {
        const { asset, ...rest } = this.targetForm;
        const payload = { ...rest, rptrMassId: asset ? asset.value : null };
        if (this.targetModal.id) {
          await ReportTargetSvc.update(this.targetModal.id, payload);
        } else {
          await ReportTargetSvc.create(payload);
        }
        this.targetModal.visible = false;
        await this.fetchTargets();
      }, 'Recipient saved.');
    },
    async searchAssets(query) {
      const response = await AssetSvc.getAssets({ params: this.buildSearchParams('massName', query) });
      return this.getRows(response).map((asset) => ({
        value: asset.massId,
        text: `${asset.massName || '-'} (ID: ${asset.massId})`,
      }));
    },
    async searchDuplicateTargetAssets(query) {
      const assets = await this.searchAssets(query);
      if (!this.duplicateModal.sourceAsset) return assets;
      return assets.filter((asset) => asset.value !== this.duplicateModal.sourceAsset.value);
    },
    fetchDeliveryLogs() {
      if (!this.canLoadLogs) return Promise.resolve();
      // Penanda SEBELUM await: watcher rentang nembak dua kali, start lalu end.
      const reqId = ++this.logsReqId;
      const params = {
        massId: this.logFilters.asset.value,
        from: this.dateToUnix(this.logFilters.dateRange.start),
        to: this.dateToUnix(this.logFilters.dateRange.end),
      };
      return this.runAction(async () => {
        const response = await ReportAssetLogSvc.getPeriod(params);
        if (reqId !== this.logsReqId) return;
        this.deliveryLogs = this.getRows(response);
      });
    },
    dateToUnix(value) {
      return Math.floor(new Date(value).getTime() / 1000);
    },
    formatLogPeriod(value) {
      if (value === null || value === undefined || value === '') return '-';
      return String(value).replace('T', ' ').replace('Z', '');
    },
    formatGeneratedAt(value) {
      if (value === null || value === undefined || value === '') return '-';
      const numericValue = Number(value);
      const date = Number.isNaN(numericValue)
        ? new Date(String(value).endsWith('Z') ? value : `${value}Z`)
        : new Date(numericValue * 1000);
      if (Number.isNaN(date.getTime())) return value;
      return `${date.toISOString().slice(0, 19).replace('T', ' ')} UTC`;
    },
    getLogStatusVariant(status) {
      if (status === 'SUCCESS') return 'success';
      if (status === 'NO_DATA') return 'warning';
      return status === 'ERROR' ? 'danger' : 'secondary';
    },
    async duplicateSelected() {
      if (this.duplicateModal.type === 'target') {
        await this.duplicateRecipientsToAssets();
      } else {
        await this.duplicateSchedulesToAssets();
      }
    },
    async duplicateRecipientsToAssets() {
      const sourceAssetId = this.duplicateModal.sourceAsset.value;
      const targetAssetIds = this.duplicateModal.targetAssets.map((item) => item.value);
      return this.runAction(async () => {
        const sourceResponse = await ReportTargetSvc.get({
          page: 1,
          max: DUPLICATE_RESULT_LIMIT,
          'finds[rptrMassId]': sourceAssetId,
        });
        const sourceItems = this.getRows(sourceResponse);
        const results = await Promise.all(targetAssetIds.map(async (targetAssetId) => {
          const existingResponse = await ReportTargetSvc.get({
            page: 1,
            max: DUPLICATE_RESULT_LIMIT,
            'finds[rptrMassId]': targetAssetId,
          });
          const existingItems = this.getRows(existingResponse);
          const existingKeys = new Set(
            existingItems.map((item) => `${(item.rptrName || '').trim().toLowerCase()}|${(item.rptrEmail || '').trim().toLowerCase()}`),
          );
          const rowsToCreate = sourceItems.filter((item) => {
            const key = `${(item.rptrName || '').trim().toLowerCase()}|${(item.rptrEmail || '').trim().toLowerCase()}`;
            return !existingKeys.has(key);
          });
          await Promise.all(rowsToCreate.map((item) => ReportTargetSvc.create({
            rptrMassId: targetAssetId,
            rptrName: item.rptrName,
            rptrEmail: item.rptrEmail,
          })));
          return {
            created: rowsToCreate.length,
            skipped: sourceItems.length - rowsToCreate.length,
          };
        }));
        const created = results.reduce((sum, item) => sum + item.created, 0);
        const skipped = results.reduce((sum, item) => sum + item.skipped, 0);
        this.duplicateModal.visible = false;
        await this.fetchTargets();
        this.message = { text: `Duplicated ${created} recipients. Skipped ${skipped} existing recipients.`, variant: 'success' };
      });
    },
    async duplicateSchedulesToAssets() {
      const sourceAssetId = this.duplicateModal.sourceAsset.value;
      const targetAssetIds = this.duplicateModal.targetAssets.map((item) => item.value);
      return this.runAction(async () => {
        const sourceResponse = await ReportTargetAssetSvc.get({
          page: 1,
          max: DUPLICATE_RESULT_LIMIT,
          'finds[mrtaMassId]': sourceAssetId,
        });
        const sourceItems = this.getRows(sourceResponse);
        const results = await Promise.all(targetAssetIds.map(async (targetAssetId) => {
          const existingResponse = await ReportTargetAssetSvc.get({
            page: 1,
            max: DUPLICATE_RESULT_LIMIT,
            'finds[mrtaMassId]': targetAssetId,
          });
          const existingItems = this.getRows(existingResponse);
          const existingKeys = new Set(existingItems.map((item) => this.buildScheduleDuplicateKey(item)));
          const rowsToCreate = sourceItems.filter((item) => !existingKeys.has(this.buildScheduleDuplicateKey(item)));
          await Promise.all(rowsToCreate.map((item) => (
            ReportTargetAssetSvc.create(this.buildAssetPayload(item, targetAssetId))
          )));
          return {
            created: rowsToCreate.length,
            skipped: sourceItems.length - rowsToCreate.length,
          };
        }));
        const created = results.reduce((sum, item) => sum + item.created, 0);
        const skipped = results.reduce((sum, item) => sum + item.skipped, 0);
        this.duplicateModal.visible = false;
        await this.fetchTargetAssets();
        this.message = { text: `Duplicated ${created} schedules. Skipped ${skipped} existing schedules.`, variant: 'success' };
      });
    },
    buildScheduleDuplicateKey(item) {
      return String(item.mrtaFrequencyType);
    },
    getTargetAssetLabel(group) {
      return `${group.assetName || '-'} (ID: ${group.massId || '-'})`;
    },
    getScheduleAssetLabel(group) {
      return `${group.assetName || '-'} (ID: ${group.massId || '-'})`;
    },
    getRecipientSummary(items) {
      if (!items.length) return '-';
      const first = items[0];
      const remaining = items.length - 1;
      if (!remaining) return `${first.rptrName || '-'} (${first.rptrEmail || '-'})`;
      return `${first.rptrName || '-'} +${remaining} recipients`;
    },
    getScheduleSummary(items) {
      if (!items.length) return '-';
      const first = items[0];
      const remaining = items.length - 1;
      const firstLabel = this.getFrequencyLabel(first.mrtaFrequencyType);
      if (!remaining) return `${firstLabel} (${first.mrtaTime || '-'})`;
      return `${firstLabel} +${remaining} schedules`;
    },
    toggleTargetGroup(item, index, event) {
      if (this.isInteractiveTarget(event)) return;
      this.$set(this.targetGroupOpenMap, item.key, !this.targetGroupOpenMap[item.key]);
    },
    toggleAssetGroup(item, index, event) {
      if (this.isInteractiveTarget(event)) return;
      this.$set(this.assetGroupOpenMap, item.key, !this.assetGroupOpenMap[item.key]);
    },
    getSelectionMap(type) {
      return type === 'target' ? this.selectedTargetMap : this.selectedAssetMap;
    },
    getGroupItemIds(type, group) {
      const field = type === 'target' ? 'rptrId' : 'mrtaId';
      return (group.items || []).map((item) => String(item[field])).filter(Boolean);
    },
    isSelected(type, id) {
      if (id === null || id === undefined) return false;
      const map = this.getSelectionMap(type);
      return Boolean(map[String(id)]);
    },
    toggleItemSelection(type, id, checked) {
      const mapKey = type === 'target' ? 'selectedTargetMap' : 'selectedAssetMap';
      this.$set(this[mapKey], String(id), Boolean(checked));
    },
    getSelectedGroupCount(type, group) {
      return this.getGroupItemIds(type, group).filter((id) => this.isSelected(type, id)).length;
    },
    isAllGroupSelected(type, group) {
      const ids = this.getGroupItemIds(type, group);
      return ids.length > 0 && ids.every((id) => this.isSelected(type, id));
    },
    isSomeGroupSelected(type, group) {
      const ids = this.getGroupItemIds(type, group);
      const selectedCount = ids.filter((id) => this.isSelected(type, id)).length;
      return selectedCount > 0 && selectedCount < ids.length;
    },
    toggleGroupSelection(type, group, checked) {
      const mapKey = type === 'target' ? 'selectedTargetMap' : 'selectedAssetMap';
      this.getGroupItemIds(type, group).forEach((id) => {
        this.$set(this[mapKey], id, Boolean(checked));
      });
    },
    clearSelection(type, ids = []) {
      const mapKey = type === 'target' ? 'selectedTargetMap' : 'selectedAssetMap';
      if (ids.length) {
        ids.forEach((id) => this.$delete(this[mapKey], String(id)));
        return;
      }
      this[mapKey] = {};
    },
    isInteractiveTarget(event) {
      if (!event || !event.target || !event.target.closest) return false;
      return Boolean(event.target.closest('button, a, input, label, .custom-control'));
    },
  },
};
</script>

<style src="@/views/master/_apiPanel.css" scoped></style>

<style scoped>
.cell-title {
  font-weight: 600;
}

.filter-panel {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.75rem;
  margin-bottom: 1rem;
  background: #fff;
}

.filter-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.asset-title {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.asset-cell {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
}

.asset-row {
  cursor: pointer;
}

.asset-rule-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.detail-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
}

.rule-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 0.6rem 0.75rem;
}

.rule-main {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.recipient-inline {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
</style>
